import { Filter, ResultSet } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { FileCopyRounded } from '@mui/icons-material';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { ascend, isNotEmpty, isNotNil, map, pipe, prop, sort } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CubeQueryData, PurchaseOrder, I18nNamespace } from '~/common/types';
import { DimPurchaseOrderField } from '~/common/types/cubes/DimPuchaseOrder';
import { DimPurchaseOrderItemField } from '~/common/types/cubes/DimPurchaseOrderItem';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';

import { PurchaseOrderDetails } from './components/PurchaseOrderDetails/PurchaseOrderDetails';
import { getPurchaseOrderHeader } from './PruchaseOrderDetailsWidget.utils';

export const PurchaseOrderDetailsWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'PurchaseOrderDetailsWidget' });

  const filters: Filter[] = [
    {
      member: DimPurchaseOrderField.ID,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const purchaseOrderQuery = useCubeQuery<CubeQueryData>({
    measures: [DimPurchaseOrderField.TOTAL_VALUE],
    filters,
    dimensions: [DimPurchaseOrderField.PRIMARY_SOURCE_REFERENCE, DimPurchaseOrderField.SUPPLIER_ID],
  });
  const purchaseOrderItemsQuery = useCubeQuery<CubeQueryData>({
    filters,
    dimensions: [
      DimPurchaseOrderItemField.ID,
      DimPurchaseOrderItemField.LINE_ITEM_NUMBER,
      DimPurchaseOrderItemField.TITLE,
      DimPurchaseOrderItemField.DESCRIPTION,
      DimPurchaseOrderItemField.UNIT_ID,
      DimPurchaseOrderItemField.PRICE_PER_UNIT,
      DimPurchaseOrderItemField.QUANTITY,
      DimPurchaseOrderItemField.CURRENCY,
    ],
  });

  const purchaseOrderItems: CubeQueryData[] | undefined = purchaseOrderItemsQuery.resultSet?.rawData();
  const purchaseOrderResultSet: ResultSet<CubeQueryData> | null = purchaseOrderQuery.resultSet;
  const hasData = isNotNil(purchaseOrderItems) && isNotEmpty(purchaseOrderItems);
  const isLoading = purchaseOrderQuery.isLoading || purchaseOrderItemsQuery.isLoading;
  const purchaseHeader = useMemo(() => getPurchaseOrderHeader(purchaseOrderResultSet), [purchaseOrderResultSet]);
  let content = <PlaceholderWidget withImage />;

  // hasData checks on lineItems only on purpose:
  // - without lineitems a PO is invalid but
  // - details from header might be (temporarily) missing and not necessarily render the PO invalid
  if (hasData) {
    const purchaseOrderData = {
      ...purchaseHeader,
      items: pipe(
        map((item: CubeQueryData) => ({
          id: item[DimPurchaseOrderItemField.ID],
          title: item[DimPurchaseOrderItemField.TITLE],
          description: item[DimPurchaseOrderItemField.DESCRIPTION],
          position: parseInt(item[DimPurchaseOrderItemField.LINE_ITEM_NUMBER]),
          unit: item[DimPurchaseOrderItemField.UNIT_ID],
          quantity: parseInt(item[DimPurchaseOrderItemField.QUANTITY]),
          pricePerUnit: parseFloat(item[DimPurchaseOrderItemField.PRICE_PER_UNIT] ?? 0),
          currency: item[DimPurchaseOrderItemField.CURRENCY],
        })),
        sort(ascend(prop('position'))),
      )(purchaseOrderItems),
    } satisfies PurchaseOrder;

    content = <PurchaseOrderDetails loading={isLoading} data={purchaseOrderData} />;
  }

  if (!isLoading && !hasData) {
    content = <PlaceholderWidget withImage />;
  }

  return (
    <Widget hover={false} data-intercom-target="PurchaseOrderDetailsWidget">
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<FileCopyRounded fontSize="small" />} />} />
      <WidgetContent>
        <div>{content}</div>
      </WidgetContent>
    </Widget>
  );
};
